import { useState, useEffect, useCallback } from 'react';
import { coreApi } from '@shared/api/client';
import { Company, CreateCompanyInput } from './types';

const INITIAL_FALLBACK_COMPANIES: Company[] = [
  {
    id: 'comp1',
    name: 'Acme Corporation',
    domain: 'acme.com',
    industry: 'Enterprise Software',
    address: '100 Innovation Way, San Francisco, CA',
    contact_count: 2,
    status: 'active',
    custom_fields: {
      annual_revenue_bracket: '$10M-$50M',
    },
    created_at: new Date().toISOString(),
  },
  {
    id: 'comp2',
    name: 'Globex Industries',
    domain: 'globex.io',
    industry: 'Advanced Robotics',
    address: '45 Tech Boulevard, Austin, TX',
    contact_count: 5,
    status: 'active',
    custom_fields: {
      annual_revenue_bracket: '$50M+',
    },
    created_at: new Date().toISOString(),
  },
];

export function useCompanies() {
  const [companies, setCompanies] = useState<Company[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchCompanies = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const data = await coreApi.get<Company[]>('/companies');
      if (Array.isArray(data)) {
        setCompanies(data);
      } else {
        setCompanies(INITIAL_FALLBACK_COMPANIES);
      }
    } catch {
      setCompanies(INITIAL_FALLBACK_COMPANIES);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCompanies();
  }, [fetchCompanies]);

  const createCompany = async (input: CreateCompanyInput): Promise<string> => {
    try {
      const created = await coreApi.post<Company>('/companies', input);
      const res = created || { ...input, id: `comp-${Date.now()}` };
      setCompanies((prev) => [res, ...prev]);
      return res.id;
    } catch {
      const local: Company = {
        ...input,
        id: `comp-${Date.now()}`,
        contact_count: 0,
        created_at: new Date().toISOString(),
      };
      setCompanies((prev) => [local, ...prev]);
      return local.id;
    }
  };

  const updateCompany = async (id: string, input: Partial<Company>) => {
    try {
      const updated = await coreApi.put<Company>(`/companies/${id}`, input);
      setCompanies((prev) => prev.map((c) => (c.id === id ? { ...c, ...updated, ...input } : c)));
    } catch {
      setCompanies((prev) => prev.map((c) => (c.id === id ? { ...c, ...input } : c)));
    }
  };

  const deleteCompany = async (id: string) => {
    try {
      await coreApi.delete(`/companies/${id}`);
    } catch {
      // ignore
    }
    setCompanies((prev) => prev.filter((c) => c.id !== id));
  };

  return {
    companies,
    isLoading,
    error,
    createCompany,
    updateCompany,
    deleteCompany,
    reload: fetchCompanies,
  };
}

export default useCompanies;
